import { action, computed, observable } from 'mobx';
import { flatten, uniq } from 'lodash';

import { Collection } from 'core/model/Collection';

import api from 'core/util/api';
import DeviceModel from './DeviceModel';
import $setup from '../$setup';

const SEARCHABLE_FIELDS = [
  'agentName',
  'appliedLabels',
  'device_manufacturer',
  'device_name',
  'deviceType',
  'flowSource',
  'flowType',
  'id',
  'ip_address',
  'model',
  'os_name',
  'os_version',
  'plan_name',
  'sending_ips',
  'serial_number',
  'site_market_name',
  'site_name',
  'vendor'
];

class DeviceCollection extends Collection<DeviceModel> {
  @observable.ref
  memoryUtilQueryData: {
    device_id: string;
    last_memory_util: number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  cpuUtilQueryData: {
    device_id: string;
    'last_total/instant': number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  temperatureQueryData: {
    device_id: string;
    avg_instant: number;
    max_instant: number;
    last_instant: number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  trafficQueryData: { i_device_name: string; f_sum_both_bytes__k_last: number; sparklineData: unknown }[] = [];

  get defaultSortState(): { field: 'device_name' | 'interfaceCount' | 'percentMatched'; direction: 'desc' | 'asc' } {
    return {
      field: 'device_name',
      direction: 'asc'
    };
  }

  get secondarySort() {
    return this.defaultSortState;
  }

  get url() {
    return '/api/ui/devices' as const;
  }

  get model() {
    return DeviceModel;
  }

  useAsyncAdd = true;

  get presetGroups() {
    return [
      {
        name: 'site_name',
        label: 'Site',
        sortBy: 'site_name',
        icon: 'map-marker'
      },
      {
        name: 'plan_id',
        label: 'Plan',
        icon: 'card_membership',
        materialIcon: true
      },
      {
        name: 'deviceType',
        label: 'Type',
        icon: 'dev-router-plain'
      },
      {
        name: 'appliedLabels',
        label: 'Label',
        sortBy: 'appliedLabels',
        icon: 'tag'
      }
    ] as const;
  }

  getFilterValues(model: DeviceModel) {
    return SEARCHABLE_FIELDS.map((name) => model[name as keyof DeviceModel] ?? model.get(name)).filter(
      (value) => value != null && value !== ''
    );
  }

  @computed
  get percentUnlabeled() {
    return this.isRequestActive('fetching') ? 0 : Math.floor((this.unlabeledDevices.length / this.size) * 100);
  }

  @computed
  get unlabeledDevices() {
    return this.models.filter((device) => !device.hasLabels);
  }

  @computed
  get deviceIdNameHash() {
    const hash: Record<string | number, string> = {};
    this.models.forEach((device) => {
      hash[device.id as string | number] = device.get('device_name') as string;
    });
    return hash;
  }

  @computed
  get deviceNameIdHash() {
    const hash: Record<string, string | number> = {};
    this.unfiltered.forEach((device) => {
      hash[device.get('device_name') as string] = device.id as string | number;
    });
    return hash;
  }

  // returns a unique array of label names for the selected models
  @computed
  get selectedLabelNames() {
    if (!this.selectedSize) {
      return [];
    }

    const labels = flatten(this.selectedArray.map((m) => (m.get('labels') as { name: string }[]).map((l) => l.name)));
    return uniq(labels);
  }

  getLinkedDeviceModel(model: DeviceModel) {
    if (model.get('device_bgp_type') === 'other_device') {
      return this.models.find((item) => item.get('id') === model.get('use_bgp_device_id')?.toString());
    }

    return null;
  }

  setMemoryUtilQueryData = () => {
    this.memoryUtilQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device && device.get('memory_util') !== result.last_memory_util) {
        device.set('memory_util', result.last_memory_util);
      }
      if (device && device.get('memory_util_timeseries') !== result.sparklineData) {
        device.set('memory_util_timeseries', result.sparklineData);
      }
    });
  };

  setCpuUtilQueryData = () => {
    this.cpuUtilQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device && device.get('cpu_util') !== result['last_total/instant']) {
        device.set('cpu_util', result['last_total/instant']);
      }
      if (device && device.get('cpu_util_timeseries') !== result.sparklineData) {
        device.set('cpu_util_timeseries', result.sparklineData);
      }
    });
  };

  setTemperatureQueryData = () => {
    this.temperatureQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device) {
        device.set('temperature', {
          avg_instant: result.avg_instant,
          max_instant: result.max_instant,
          last_instant: result.last_instant
        });
      }
      if (device && device.get('temperature_timeseries') !== result.sparklineData) {
        device.set('temperature_timeseries', result.sparklineData);
      }
    });
  };

  setTrafficQueryData = () => {
    const { deviceNameIdHash } = this;
    this.trafficQueryData.forEach((result) => {
      const id = deviceNameIdHash[result.i_device_name];
      const device = id ? this.get(id) : null;
      if (device && device.get('traffic') !== result.f_sum_both_bytes__k_last) {
        device.set('traffic', result.f_sum_both_bytes__k_last);
      }
      if (device && device.get('traffic_timeseries') !== result.sparklineData) {
        device.set('traffic_timeseries', result.sparklineData);
      }
    });
  };

  async bulkDelete(options = {}) {
    const { selectedArray } = this;
    return super
      .bulkDelete({ ...options, removeFromCollection: false })
      .then((result: { hasErrors?: boolean; errors?: { deviceId: string | number }[] }) => {
        selectedArray
          ?.filter(
            (device: DeviceModel) =>
              !result.hasErrors || !result.errors?.find((errDevice) => errDevice.deviceId === device.id)
          )
          .forEach((model) => {
            if (model.isArchived) {
              this.remove(model.id);
            } else {
              model.set('device_status', 'D');
            }
          });

        return result;
      });
  }

  async bulkUpdateSite(url: string, data: { value: string | number; ids: (string | number)[] }) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        name: site_name,
        site_market_id,
        site_market_name
      } = await api.post(`${url}/bulkUpdateSite`, { data });
      // update the site for the devices which were ACTUALLY updated.
      // note that this could be different from the devices which were requested to update due to RBAC permissions
      // TODO we should probably have better UX for this but for now we'll consider it a corner case
      ids.forEach((id: string) => {
        this.get(id)?.safeSet({ site_id: data.value, site_market_id, site_market_name, site_name });
      });
    } finally {
      this.setRequestStatus(null);
    }
  }

  @action
  async bulkUpdatePlan(url: string, data: { value: string | number; ids: (string | number)[] }) {
    this.setRequestStatus('updating');
    try {
      const { ids, plan_name } = await api.post(`${url}/bulkUpdatePlan`, { data });
      // update the plan for the devices which were ACTUALLY updated.
      // note that this could be different from the devices which were requested to update due to RBAC permissions
      // TODO we should probably have better UX for this but for now we'll consider it a corner case

      // we will dynamically update plan device counts in order to avoid fetching this entire table
      const decrements: Record<string, number> = {};
      ids.forEach((id: string) => {
        const prevPlan = this.get(id)?.get('plan_id') as string;
        decrements[prevPlan] = (decrements[prevPlan] || 0) + 1;
        this.get(id)?.safeSet({ plan_id: data.value, plan_name });
      });

      // update $setup plans collection with new device counts
      $setup.plans = $setup.plans.map((plan) => {
        if (String(plan.id) === String(data.value)) {
          plan.device_count += ids.length;
        }
        if (decrements[plan.id]) {
          plan.device_count -= decrements[plan.id];
        }
        return plan;
      });
    } finally {
      this.setRequestStatus(null);
    }
  }

  async bulkUpdate(
    name: 'plan_id' | 'site_id' | 'kentik_agent_snmp_options.agent_id',
    value: unknown
  ): Promise<void | null> {
    if (name === 'kentik_agent_snmp_options.agent_id') {
      return super.bulkUpdate(name, value);
    }
    const { url } = this;
    const { selectedIds } = this;
    if (!selectedIds?.length || (typeof value !== 'string' && typeof value !== 'number')) {
      return null;
    }
    const data = { ids: selectedIds, value };
    return name === 'site_id' ? this.bulkUpdateSite(url, data) : this.bulkUpdatePlan(url, data);
  }
}

export default DeviceCollection;
